import React, { useEffect, useState } from 'react';
import { TabPanel, Tabs } from 'tea-component';
import { getGameKeywordFilter } from '@/pages/opinion/services';
import type { KeyWordsType } from '../../types';
import { commonBackInfo } from '../../utils';
import { useKeywordDefaultValue } from '../../hooks/init';
import useGetRequest from '../../hooks/useGetRequest';
import DropdownBox from '../../components/DropdownBox';
import SystemRecommendations from './SystemRecommendations';
import CustomInput from './CustomInput';
import KeywordsModeHeader from './KeywordsModeHeader';
import { placeholderMap } from '../../constants/placeholder';
import { isValidExpression, parseRule } from './utils';
import { addFilterBuriedPoint } from '../../utils/point';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import {
  keywordModalTextMap,
  keywordModeTextMap,
} from '../../constants/keyword';
import useLanguage from '@op/hooks/useLanguage';
import classNames from 'classnames';
import { ClassSpecial } from '@op/constants/common';
import './index.scss';

const KeyWordsSelect = ({
  gameInfo,
  onChange,
  defaultValue,
  filterValue,
  oriDefaultFilter,
  backOriDefaultValue,
  lang,
  hideSystemTab = false,
}: KeyWordsType) => {
  const { formatMessage } = useLanguage();

  const {
    values,
    setValues,
    vagueValues,
    setVagueValues,
    excludeValues,
    setExcludeValues,
    keywordActiveId,
    preKeywordActiveId,
    setKeywordActiveId,
    keywordCustomInput,
    setKeywordCustomInput,
    diffClassName,
    customInputClose,
    customInputConfirm,
    customKeywordList,
    initKeywordSelect,
    keywordSearchType,
    setKeywordSearchType,
    renderList,
    grammarTrue,
    grammarText,
    keywordListMode,
    setKeywordListMode,
  } = useKeywordDefaultValue({
    defaultValue,
    filterValue,
    dealwithCustomInput: parseRule,
    hideSystemTab,
  });

  const getButtonElement = () => {
    if (keywordActiveId === 'system') {
      const len = values.length + excludeValues.length + vagueValues.length;
      if (len === 0) return formatMessage({ id: 'All' });
      if (len > 1) return len;
      return len;
      return (
        <div className="dbo-key-word-select-text">{values.join(', ')}</div>
      );
    }

    return keywordCustomInput ? keywordCustomInput : formatMessage('All');
  };

  const renderButtonElement = () => {
    const text = getButtonElement();
    const isCustom = keywordActiveId === 'custom';

    // 根据需求显示不同文本:
    // Exact Search + Include: Keyword (Exact Search-Include)
    // Exact Search + Exclude: Keyword (Exact Search-Exclude)
    // System + Include: Keyword (Include)
    // System + Exclude: Keyword (Exclude)
    const modeText = formatMessage({ id: keywordModeTextMap[keywordListMode] });
    const bracketText = isCustom
      ? `${formatMessage({
          id: keywordModalTextMap[keywordSearchType],
        })}-${modeText}`
      : modeText;

    return (
      <div style={{ display: 'flex' }}>
        <span className="select-button-text">
          {placeholderMap.keyWords?.[lang]} ({bracketText})
        </span>
        <span>{text}</span>
      </div>
    );
  };

  function getKeywordFilterValue() {
    let deleteKeys: Array<string> = ['keywords_list', 'merge_keywords_list'];
    let data: any = {
      keyword: values,
      isKeywordFilter: true,
      keywordActiveId,
      like_math_keywords: vagueValues,
      like_math_exclude_keywords: excludeValues,
      keywords_list_mode: keywordListMode,
    };
    if (keywordActiveId === 'custom') {
      deleteKeys = [
        'keyword',
        'like_math_keywords',
        'like_math_exclude_keywords',
      ];
      const isExtend = keywordSearchType === 'extend';
      deleteKeys.push(isExtend ? 'keywords_list' : 'merge_keywords_list');
      data = {
        isKeywordFilter: true,
        [isExtend ? 'merge_keywords_list' : 'keywords_list']: customKeywordList,
        keywordActiveId,
        keywordCustomInput,
        keywordSearchType,
        keywords_list_mode: keywordListMode,
      };
    }

    return { deleteKeys, data };
  }

  useEffect(() => {
    const text = getButtonElement();
    const { data, deleteKeys } = getKeywordFilterValue();
    onChange?.(
      'keyword',
      commonBackInfo(data, {
        keyWords: text,
        keywordSearchType,
        keywordActiveId,
        keywordCustomInput,
        keywordListMode,
      }),
      deleteKeys,
    );
  }, [
    values,
    excludeValues,
    vagueValues,
    formatMessage,
    customKeywordList,
    keywordActiveId,
    keywordSearchType,
    keywordListMode,
  ]);

  const [total, setTotal] = useState(0);
  const keywordProps = useGetRequest({
    gameInfo,
    params: { filter_types: ['config', 'nlp'], top_n: 100 },
    requestFunc: getGameKeywordFilter as any,
    singleKey: 'keyword',
    hasMultiple: false,
    dealwithData: (data: any) => {
      setTotal(data?.total || 0);
      return data?.list;
    },
  });

  function addBuriedPoint(dataInfo?: any) {
    if (!dataInfo) {
      const { data } = getKeywordFilterValue();
      dataInfo = data;
    }
    addFilterBuriedPoint(FilterButtonPoint.keywordFilterType, dataInfo);
  }

  const tabs = [{ id: 'custom', label: formatMessage({ id: 'Custom Input' }) }];
  if (!hideSystemTab) {
    tabs.push({
      id: 'system',
      label: formatMessage({ id: 'System Recommendations' }),
    });
  }

  return (
    <div className={`dbo-keywords-select  ${diffClassName}`}>
      <DropdownBox
        buttonElement={renderButtonElement}
        matchButtonWidth={false}
        onClose={() => {
          const isCustomTab = keywordActiveId === 'custom';
          const preIsCustomTab = preKeywordActiveId === 'custom';
          const isEmptyText = keywordCustomInput === '';

          // custom → custom + 输入有内容 ✅
          if (isCustomTab && !isEmptyText) {
            const textIsTrue = isValidExpression(keywordCustomInput);
            // 有效输入 ✅
            if (textIsTrue) {
              customInputConfirm(parseRule(keywordCustomInput));
              addBuriedPoint();
              return;
            }
            // 无效输入，关闭并恢复 ✅
            customInputClose();
            return;
          }

          // custom → custom + 输入无内容 返回[] ✅
          if (preIsCustomTab && isCustomTab) {
            initKeywordSelect();
            customInputConfirm();
            addBuriedPoint({
              keyword: [],
              isKeywordFilter: true,
              keywordActiveId: 'system',
              like_math_keywords: [],
              like_math_exclude_keywords: [],
            });
            return;
          }

          // system → custom + 空输入 → 关闭并恢复 ✅
          if (!preIsCustomTab && isCustomTab) {
            customInputClose();
            return;
          }

          // 其他情况，清空选中 ✅
          customInputConfirm();
          addBuriedPoint();
        }}
      >
        {(close: any) => {
          return (
            <>
              <KeywordsModeHeader
                keywordListMode={keywordListMode}
                setKeywordListMode={setKeywordListMode}
              />
              <Tabs
                tabs={tabs}
                activeId={keywordActiveId}
                onActive={(tab: any) => setKeywordActiveId(tab.id)}
                className={classNames('dbo-keywords-select-tabs', {
                  [ClassSpecial.TAB_NONE]: hideSystemTab,
                })}
              >
                {!hideSystemTab && (
                  <TabPanel id="system">
                    <SystemRecommendations
                      {...keywordProps}
                      values={values}
                      setValues={setValues}
                      vagueValues={vagueValues}
                      setVagueValues={setVagueValues}
                      excludeValues={excludeValues}
                      setExcludeValues={setExcludeValues}
                      total={total}
                    ></SystemRecommendations>
                  </TabPanel>
                )}
                <TabPanel id="custom">
                  <CustomInput
                    customInput={keywordCustomInput}
                    setCustomInput={setKeywordCustomInput}
                    keywordSearchType={keywordSearchType}
                    setKeywordSearchType={setKeywordSearchType}
                    renderList={renderList}
                    grammarText={grammarText}
                    grammarTrue={grammarTrue}
                    className={classNames({'dbo-custom-input-diff': hideSystemTab })}
                  ></CustomInput>
                </TabPanel>
              </Tabs>
            </>
          );
        }}
      </DropdownBox>
    </div>
  );
};

export default KeyWordsSelect;
